import React, { forwardRef, memo, useCallback, useState } from 'react';
import { defineMessages, type IntlShape, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import LinkIcon from '@atlaskit/icon/core/link';
import CustomItem from '@atlaskit/menu/custom-item';
import type { CustomItemComponentProps } from '@atlaskit/menu/types';
import type { TriggerProps } from '@atlaskit/popup/types';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Tile from '@atlaskit/tile/tile';
import { token } from '@atlaskit/tokens';

import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { LoadableLazy } from '@confluence/loadable';
import { scrollToElement } from '@confluence/scroll/entry-points/scrollToElement.ts';

import {
	ANALYTICS_SOURCE,
	CONNECTION_ROW_ACTION_SUBJECT_ID,
	SCROLL_RESULT_ACTION_SUBJECT_ID,
	type SmartLinkScrollResult,
} from './analytics.ts';
import type { ConnectionItemHandler, RefreshLinkUrlHandler } from './ConnectionsByLineMenu.tsx';
import { ConnectionsByLineRowActions } from './ConnectionsByLineRowActions.tsx';
import { useConnectionRowActions } from './hooks/useConnectionRowActions.ts';
import { useConnectSmartLinkAccount } from './hooks/useConnectSmartLinkAccount.ts';
import {
	findRenderedSmartLinkElement,
	getSmartLinkContentRoot,
} from './utils/findRenderedSmartLinkElement.ts';
import { SmartLinkIconRenderer } from './SmartLinkIconRenderer.tsx';
import type { EnhancedSmartLink } from './utils/smartLinkTypes.ts';
import { formatProduct } from './utils/formatProduct.ts';
import { getDescription } from './utils/getDescription.ts';
import { getSmartLinkIcon } from './utils/smartLinkIcon.ts';
import { useTwgAnalyticsEvents } from './useTwgAnalyticsEvents.ts';

const UnconnectedConnectorHoverCardLoadable = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-UnconnectedConnectorHoverCard" */ './UnconnectedConnectorHoverCard.tsx'
			)
		).UnconnectedConnectorHoverCard,
});

const i18n = defineMessages({
	connect: {
		id: 'byline-connections.menu.item.connect',
		defaultMessage: 'Connect',
		description:
			'Action label shown on the right of an unconnected connections menu item, prompting the user to connect the related product.',
	},
	connecting: {
		id: 'byline-connections.menu.item.connecting',
		defaultMessage: 'Connecting...',
		description:
			'Status label shown on the right of an unconnected connections menu item while the related product account is being connected.',
	},
	descriptionSeparator: {
		id: 'byline-connections.menu.item.description-separator',
		defaultMessage: '{left} • {right}',
		description:
			'Joins two parts of a connections menu item secondary line with a bullet separator (e.g. resource type, who, and when).',
	},
	productLink: {
		id: 'byline-connections.menu.item.product-link',
		defaultMessage: '{product} link',
		description:
			'Fallback title for an incoming connections menu item that has no human-readable title, naming the source product (e.g. "Slack link", "Google Docs link").',
	},
});

const styles = cssMap({
	textColumn: {
		minWidth: 0,
		flex: 1,
	},
	title: {
		font: token('font.body'),
		fontWeight: token('font.weight.regular'),
		color: token('color.text'),
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	description: {
		font: token('font.body.small'),
		fontWeight: token('font.weight.regular'),
		color: token('color.text.subtlest'),
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	// CustomItem treats children as the menu title and defaults to nowrap.
	// The summary needs a wrapping context so Text can clamp it to two lines.
	aiSummary: {
		whiteSpace: 'normal',
	},
	trailing: {
		flexShrink: 0,
		display: 'inline-flex',
		alignItems: 'center',
	},
});

const pressableStyles = cssMap({
	connect: {
		font: token('font.body'),
		color: token('color.link'),
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		cursor: 'pointer',
		paddingBlock: token('space.0'),
		paddingInline: token('space.050'),
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'underline',
		},
	},
	connecting: {
		font: token('font.body'),
		color: token('color.text.disabled'),
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		cursor: 'default',
		paddingBlock: token('space.0'),
		paddingInline: token('space.050'),
		textDecoration: 'none',
	},
});

type RowContent = {
	titleContent: string | undefined;
	descriptionContent: string | undefined | null;
};

/**
 * Builds the title/description for a non-granted byline row (unauthorized,
 * forbidden, not_found, or unresolved). The link resolver doesn't return a
 * usable title/description for these, so we derive one from the URL itself:
 * the path becomes the title and the hostname becomes the description.
 *
 *   https://example.com/foo/bar?x=1#h  →  { titleContent: 'foo/bar?x=1#h', descriptionContent: 'example.com' }
 *   https://www.example.com/           →  { titleContent: 'example.com',  descriptionContent: 'example.com' }
 *   'not a url'                         →  { titleContent: 'not a url',    descriptionContent: null }
 */
const getNonGrantedRowContent = (rawUrl: string): RowContent => {
	let parsed: URL;
	try {
		parsed = new URL(rawUrl);
	} catch {
		// URL was unparseable — fall back to showing the raw URL.
		return { titleContent: rawUrl, descriptionContent: null };
	}

	const hostname = parsed.hostname.replace(/^www\./i, '');
	// Only trim slashes from the pathname itself — slashes inside the query
	// or fragment are meaningful and must be preserved.
	const trimmedPath = parsed.pathname.replace(/^\/+|\/+$/g, '');
	const pathDisplay = `${trimmedPath}${parsed.search}${parsed.hash}` || hostname;

	return { titleContent: pathDisplay, descriptionContent: hostname };
};

/**
 * Builds the title/description for a granted byline row from the data the
 * link resolver returned (title, plus 1–2 description slots).
 */
const getGrantedRowContent = (
	item: EnhancedSmartLink,
	locale: string,
	formatMessage: IntlShape['formatMessage'],
): RowContent => {
	const parts = getDescription(item, locale).filter((slot): slot is string => Boolean(slot));
	const descriptionContent =
		parts.length === 0
			? null
			: parts.reduce((left, right) => formatMessage(i18n.descriptionSeparator, { left, right }));
	return { titleContent: item.title, descriptionContent };
};

/**
 * Builds the title/description for a non-granted incoming byline row —
 * typically a third-party Teamwork Graph link created with `access: undefined`.
 * These often carry no resolver title, which would otherwise leave the row
 * showing a raw URL, so we prefer a human-readable title when the graph gave us
 * one, else a "{Product} link" label (e.g. "Slack link"), and only keep the
 * URL-derived fallback when neither a title nor a product is available. The
 * description stays the hostname in those fallback cases.
 */
const getIncomingRowContent = (
	item: EnhancedSmartLink,
	formatMessage: IntlShape['formatMessage'],
): RowContent => {
	const { descriptionContent } = getNonGrantedRowContent(item.url);
	if (item.title) {
		return { titleContent: item.title, descriptionContent };
	}
	const product = formatProduct(item.product);
	if (product) {
		return {
			titleContent: formatMessage(i18n.productLink, { product }),
			descriptionContent,
		};
	}
	return getNonGrantedRowContent(item.url);
};

/**
 * Selects and builds the row content for a byline row: granted rows use the
 * resolver data, non-granted incoming rows get the incoming-specific title
 * handling, and everything else falls back to the URL.
 */
const getRowContent = (
	item: EnhancedSmartLink,
	linkDirection: LinkDirection,
	locale: string,
	formatMessage: IntlShape['formatMessage'],
): RowContent => {
	if (item.access === 'granted') {
		return getGrantedRowContent(item, locale, formatMessage);
	}
	if (linkDirection === 'incoming') {
		return getIncomingRowContent(item, formatMessage);
	}
	return getNonGrantedRowContent(item.url);
};

type RowComponentProps = CustomItemComponentProps & {
	onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
	onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
};

// `CustomItem`'s `component` prop is typed as
// `ComponentType<PropsWithChildren<TCustomComponentProps>>`, but `forwardRef`
// produces `ForwardRefExoticComponent<Omit<Props, "ref"> & RefAttributes<…>>`
// which strips `ref` from the props and so doesn't structurally satisfy
// `ComponentType<Props>`. We cast back to `ComponentType<…>` here: the cast
// is sound because `CustomItem` reads `ref` separately from props and
// forwards it via React's ref-forwarding mechanism (which is exactly what
// `forwardRef` is designed for).
const RowComponent = forwardRef<HTMLDivElement, RowComponentProps>(
	({ children, ...props }, ref) => (
		<div ref={ref} {...props}>
			{children}
		</div>
	),
) as unknown as React.ComponentType<React.PropsWithChildren<RowComponentProps>>;

type ConnectButtonProps = {
	item: EnhancedSmartLink;
	refreshLinkUrl: RefreshLinkUrlHandler;
	onConnectSuccess?: () => void;
};

const ConnectButton = ({ item, refreshLinkUrl, onConnectSuccess }: ConnectButtonProps) => {
	const { formatMessage } = useIntl();
	const { connect, isConnecting } = useConnectSmartLinkAccount({
		item,
		refreshLinkUrl,
		onConnectSuccess,
	});

	return (
		<Box xcss={styles.trailing}>
			<Pressable
				xcss={isConnecting ? pressableStyles.connecting : pressableStyles.connect}
				onClick={connect}
				isDisabled={isConnecting}
			>
				{formatMessage(isConnecting ? i18n.connecting : i18n.connect)}
			</Pressable>
		</Box>
	);
};

export type LinkDirection = 'incoming' | 'outgoing';

export type ConnectionsByLineMenuItemProps = {
	item: EnhancedSmartLink;
	refreshLinkUrl?: RefreshLinkUrlHandler;
	linkDirection: LinkDirection;
	/**
	 * The cc-smarts one-liner about this link. When present it replaces the metadata
	 * line so the row is title + one-liner. The byline popup and Appears in / Links
	 * to rows pass none and keep title + metadata.
	 */
	aiSummary?: string;
	onConnectSuccess?: () => void;
	onHover?: ConnectionItemHandler;
	onNavigate?: () => void;
	/** Called immediately before the row invokes its Smart Link preview action. */
	onPreviewOpen?: () => void;
	/**
	 * Switches the Teamwork Graph modal to Rovo chat, seeded with the given prefilled
	 * prompt.
	 */
	onAskRovo?: (prompt?: string) => void;
	/**
	 * Title-only row: hides the description and AI summary. Every popup menu section
	 * uses this; the preview panel keeps the two-line row.
	 */
	compact?: boolean;
	/**
	 * Static row: suppresses every hover affordance — the Connect button, the trailing
	 * row-action cluster, and the unconnected-connector hover card — and never reports a
	 * hover. Every popup menu section uses this so its rows stay a plain list; the
	 * preview panel is where those actions live.
	 */
	disableHover?: boolean;
};

const ConnectionsByLineMenuItemInner = ({
	item,
	refreshLinkUrl,
	linkDirection,
	aiSummary,
	onConnectSuccess,
	onHover,
	onNavigate,
	onPreviewOpen,
	onAskRovo,
	compact,
	disableHover,
}: ConnectionsByLineMenuItemProps): React.JSX.Element => {
	const { formatMessage, locale } = useIntl();
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();
	const isLivePage = useIsCurrentPageLive();
	const [isRowHovered, setIsRowHovered] = useState(false);
	const isGranted = item.access === 'granted';
	const rowActions = useConnectionRowActions(item, onPreviewOpen, onAskRovo);

	const fireItemClickAnalytics = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'connectionRow',
				actionSubjectId: CONNECTION_ROW_ACTION_SUBJECT_ID,
				source: ANALYTICS_SOURCE,
				attributes: {
					product: item.product,
					resourceType: item.resourceType,
					access: item.access,
					occurrenceIndex: item.occurrenceIndex,
					isLivePage,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, isLivePage, item]);

	const fireScrollResultAnalytics = useCallback(
		(result: SmartLinkScrollResult) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: result === 'success' ? 'succeeded' : 'failed',
					actionSubject: 'smartLinkScroll',
					actionSubjectId: SCROLL_RESULT_ACTION_SUBJECT_ID,
					source: ANALYTICS_SOURCE,
					attributes: {
						result,
						reason: result === 'success' ? undefined : result,
						product: item.product,
						resourceType: item.resourceType,
						occurrenceIndex: item.occurrenceIndex,
						isLivePage,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, isLivePage, item],
	);

	const handleRowClick = useCallback(() => {
		fireItemClickAnalytics();
		if (linkDirection === 'incoming') {
			window.open(item.url, '_blank', 'noopener,noreferrer');
			onNavigate?.();
			return;
		}

		const root = getSmartLinkContentRoot(isLivePage);
		if (!root) {
			fireScrollResultAnalytics('not_found');
			return;
		}

		const result = findRenderedSmartLinkElement({
			root,
			url: item.url,
		});

		if (result.status !== 'found') {
			fireScrollResultAnalytics(result.status);
			return;
		}

		try {
			scrollToElement(result.element, undefined, 'smooth', isLivePage);
			fireScrollResultAnalytics('success');
			onNavigate?.();
		} catch {
			fireScrollResultAnalytics('scroll_error');
		}
	}, [
		fireItemClickAnalytics,
		fireScrollResultAnalytics,
		isLivePage,
		item,
		linkDirection,
		onNavigate,
	]);

	const handleMouseEnter = useCallback(() => {
		if (disableHover) {
			return;
		}
		setIsRowHovered(true);
		onHover?.(item);
	}, [disableHover, onHover, item]);

	const handleMouseLeave = useCallback(() => {
		setIsRowHovered(false);
	}, []);

	// `forbidden` means the user is authenticated to the provider but lacks
	// permission to this specific resource (e.g. a private Google Drive doc) —
	// connecting an account can't grant access, so don't offer Connect. (Note:
	// `meta.visibility` is unreliable here: accessible docs report 'restricted'
	// while the private one reports 'not_found', so we key on `access`.)
	const showConnectAction =
		!isGranted &&
		item.access !== 'forbidden' &&
		item.authProviders?.length > 0 &&
		// Without a refresher there is nothing to update the row after a successful
		// auth, so the Connect flow would dead-end — don't offer it at all.
		Boolean(refreshLinkUrl);

	const { titleContent, descriptionContent } = getRowContent(
		item,
		linkDirection,
		locale,
		formatMessage,
	);
	const icon = getSmartLinkIcon(item);

	const rowContent = (
		<Inline space="space.150" alignBlock="center">
			<Tile label="" size="small" backgroundColor="transparent" hasBorder>
				{icon ? (
					<SmartLinkIconRenderer icon={icon} size="menu" />
				) : (
					<LinkIcon label="" color={token('color.icon.subtle')} />
				)}
			</Tile>
			<Box xcss={styles.textColumn}>
				{compact ? (
					<Box xcss={styles.title}>{titleContent}</Box>
				) : (
					<Stack space="space.025">
						<Box xcss={styles.title}>{titleContent}</Box>
						{aiSummary ? (
							<Box xcss={styles.aiSummary}>
								<Text as="p" size="small" weight="regular" color="color.text.subtlest" maxLines={2}>
									{aiSummary}
								</Text>
							</Box>
						) : (
							<Box xcss={styles.description}>{descriptionContent}</Box>
						)}
					</Stack>
				)}
			</Box>
			{/* Revealed on hover only, in step with the row-action cluster below, so the trailing
			    affordances behave the same across every menu section. The unconnected-connector
			    hover card still opens on the same hover, so the Connect prompt and its preview
			    appear together. */}
			{showConnectAction && refreshLinkUrl && isRowHovered ? (
				<ConnectButton
					item={item}
					refreshLinkUrl={refreshLinkUrl}
					onConnectSuccess={onConnectSuccess}
				/>
			) : null}
			{isRowHovered && rowActions.length > 0 ? (
				<ConnectionsByLineRowActions item={item} actions={rowActions} />
			) : null}
		</Inline>
	);

	const customItem = (triggerRef?: React.Ref<HTMLDivElement>) => (
		<CustomItem<RowComponentProps>
			component={RowComponent}
			ref={triggerRef}
			shouldTitleWrap
			onClick={handleRowClick}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			{rowContent}
		</CustomItem>
	);

	// `disableHover` rows never open the hover card, so skip the lazy wrapper entirely
	// rather than mount it with a permanently-closed `isOpen`.
	if (showConnectAction && !disableHover) {
		return (
			<UnconnectedConnectorHoverCardLoadable item={item} isOpen={isRowHovered}>
				{(triggerProps: TriggerProps) => customItem(triggerProps.ref as React.Ref<HTMLDivElement>)}
			</UnconnectedConnectorHoverCardLoadable>
		);
	}

	return customItem();
};

export const ConnectionsByLineMenuItem: React.MemoExoticComponent<
	({
		item,
		refreshLinkUrl,
		linkDirection,
		aiSummary,
		onConnectSuccess,
		onHover,
		onNavigate,
		onPreviewOpen,
		onAskRovo,
		compact,
		disableHover,
	}: ConnectionsByLineMenuItemProps) => React.JSX.Element
> = memo(ConnectionsByLineMenuItemInner);
